import type { ReactNode } from "react";

interface SpinnerProps {
  /** Размер спиннера */
  size?: "sm" | "md" | "lg" | "xl";
  /** Текст под спиннером */
  text?: string;
  /** Полноэкранный режим */
  fullScreen?: boolean;
  /** Цвет спиннера */
  variant?: "primary" | "white";
}

const sizeClasses = {
  sm: "w-6 h-6 border-2",
  md: "w-10 h-10 border-3",
  lg: "w-16 h-16 border-4",
  xl: "w-24 h-24 border-[5px]",
};

const textSizeClasses = {
  sm: "text-sm mt-2",
  md: "text-base mt-3",
  lg: "text-lg mt-4",
  xl: "text-xl mt-5",
};

export const Spinner = ({
  size = "md",
  text,
  fullScreen = false,
  variant = "primary",
}: SpinnerProps) => {
  const spinnerColorClass =
    variant === "primary"
      ? "border-primary border-t-transparent"
      : "border-white border-t-transparent";

  const textColorClass = variant === "primary" ? "text-gray-600" : "text-white";

  const spinner = (
    <div className="flex flex-col items-center justify-center">
      {/* Анимированный круг */}
      <div
        className={`
          ${sizeClasses[size]}
          ${spinnerColorClass}
          rounded-full
          animate-spin
        `}
        role="status"
        aria-label="Загрузка"
      />

      {/* Текст под спиннером */}
      {text && (
        <p
          className={`
            ${textSizeClasses[size]}
            ${textColorClass}
            font-medium
            animate-pulse
          `}
        >
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
        {spinner}
      </div>
    );
  }

  return spinner;
};

/** Компонент для загрузки страницы */
export const PageSpinner = ({ text }: { text?: string }) => {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Spinner size="lg" text={text || "Загрузка..."} />
    </div>
  );
};

/** Компонент для загрузки в кнопке */
export const ButtonSpinner = () => {
  return (
    <div className="inline-flex items-center gap-2">
      <div
        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
        role="status"
        aria-label="Загрузка"
      />
      <span>Загрузка...</span>
    </div>
  );
};

/** Компонент для inline загрузки */
export const InlineSpinner = ({ text }: { text?: string }) => {
  return (
    <div className="inline-flex items-center gap-3">
      <div
        className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"
        role="status"
        aria-label="Загрузка"
      />
      {text && <span className="text-body text-gray-600">{text}</span>}
    </div>
  );
};

/** Компонент для загрузки с кастомным содержимым */
export const LoadingContainer = ({
  isLoading,
  children,
  loadingText,
  spinnerSize = "lg",
}: {
  isLoading: boolean;
  children: ReactNode;
  loadingText?: string;
  spinnerSize?: "sm" | "md" | "lg" | "xl";
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size={spinnerSize} text={loadingText || "Загрузка..."} />
      </div>
    );
  }

  return <>{children}</>;
};
